import { useEffect } from 'react';
import { SITE, SITE_NAME, clampDescription } from '../seo/libraryMeta';

/*
 * Per-route <head> metadata for a single-page app.
 *
 * Everything this writes is set after render, which is fine: Googlebot and
 * Bingbot execute JavaScript and read the resulting DOM. It is not fine for
 * link-preview scrapers — Slack, WhatsApp, LinkedIn and X do not run JS and
 * will only ever see the static tags in public/index.html. So the static file
 * carries sane sitewide defaults, and this refines them for crawlers.
 *
 * Hand-rolled rather than react-helmet: one dependency fewer, and the whole
 * behaviour is thirty lines of upsert.
 *
 * Tags are marked data-seo so a route change can remove the previous route's
 * tags. Without that marker we would either accumulate duplicates or have to
 * delete tags the HTML shipped with.
 */

const DEFAULT_IMAGE = `${SITE}/icon-512.png`;

const abs = (u) => {
  if (!u) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(u)) return u;
  return `${SITE}${u.startsWith('/') ? '' : '/'}${u}`;
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  el.setAttribute('data-seo', '1');
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute('data-seo', '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(data) {
  document.head.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
  if (!data) return;
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.setAttribute('data-seo-jsonld', '1');
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
}

/**
 * @param title        page title; " · LearnXops" is appended unless `rawTitle`
 * @param description  meta description — aim for 70-160 characters
 * @param canonical    absolute URL this page should consolidate to. For
 *                     articles that is the www.learnxops.com copy, not this one.
 * @param noindex      true for auth and signed-in product routes
 * @param image        social image; relative paths are made absolute
 * @param type         og:type, "article" for posts
 * @param jsonLd       structured data object
 */
export function useSeo({
  title, description, canonical, noindex = false,
  image, type = 'website', rawTitle = false, jsonLd = null,
} = {}) {
  const fullTitle = !title ? SITE_NAME : (rawTitle ? title : `${title} · ${SITE_NAME}`);
  // Serialised so a freshly-built object on every render does not re-run this.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');

    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:image', abs(image));
    // `null` means "deliberately no canonical" (a not-found view); `undefined`
    // means "use the current path". `||` would collapse the two.
    const selfUrl = `${SITE}${window.location.pathname}`;
    const canonicalHref = canonical === null ? null : (canonical || selfUrl);
    upsertMeta('property', 'og:url', canonicalHref || selfUrl);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', abs(image));

    if (canonicalHref) upsertLink('canonical', canonicalHref);
    else document.head.querySelectorAll('link[rel="canonical"][data-seo]').forEach((n) => n.remove());
    setJsonLd(jsonLdKey ? JSON.parse(jsonLdKey) : null);

    // Tear down on unmount. Without this, navigating to a route that sets no
    // metadata of its own leaves the previous route's canonical and JSON-LD in
    // the DOM — so a crawler rendering that route is told it is a copy of the
    // page the visitor came from. Title and description are harmless to leave:
    // the next route overwrites them, and the static defaults remain valid.
    return () => {
      document.head.querySelectorAll('link[rel="canonical"][data-seo]').forEach((n) => n.remove());
      document.head.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
    };
  }, [fullTitle, description, canonical, noindex, image, type, jsonLdKey]);
}

/*
 * Component form, for pages with a single return. Pages that return early
 * (loading, gated, error) should call useSeo at the top instead, so the tags
 * are set on every branch rather than only the happy one.
 */
export default function Seo(props) {
  useSeo(props);
  return null;
}

/*
 * Re-exported, not redefined. clampDescription and the site constants live in
 * src/seo/libraryMeta.js so that scripts/prerender.js — plain node, no
 * webpack — produces byte-identical metadata to what this hook writes.
 */
export { clampDescription, SITE, SITE_NAME };
